const SUPPORT_AI_CUSTOMER_DOCS_ORIGIN = 'https://docs.avalai.ir';
const SUPPORT_AI_CUSTOMER_DOCS_AUTHORITY = 'docs.avalai.ir';
const SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH = '/platform/support/knowledge-base';
/**
 * Account-data sources: the bot cites the customer's own dashboard pages (wallet, keys, usage…).
 * Exact paths only, no query or hash — mirrors SUPPORT_AI_ACCOUNT_ALLOWED_PATHS on the server.
 */
export const SUPPORT_AI_CUSTOMER_ACCOUNT_PATHS: ReadonlySet<string> = new Set([
  '/platform/billing',
  '/platform/billing/credit',
  '/platform/billing/packages',
  '/platform/billing/history',
  '/platform/api-keys',
  '/platform/usage/overview',
  '/platform/usage/reports',
  '/platform/limits'
]);
/** Tool names the server may report in `customer_ai.account_tools`; anything else is dropped. */
export const SUPPORT_AI_CUSTOMER_ACCOUNT_TOOLS = [
  'wallet',
  'credit_sources',
  'package_catalog',
  'api_keys',
  'usage_stats',
  'usage_reports',
  'subscription',
  'payments'
] as const;
export type SupportAICustomerAccountTool = (typeof SUPPORT_AI_CUSTOMER_ACCOUNT_TOOLS)[number];
const SUPPORT_AI_CUSTOMER_SOURCE_HREF_MAX_LENGTH = 2048;
const MAX_URL_DECODE_PASSES = 4;
const UNSAFE_URL_TEXT_RE = /[\u0000-\u001F\u007F-\u009F\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069<>"'`]/u;
const URL_WHITESPACE_RE = /\s/u;

/** Browser timers use a signed 32-bit delay; this is not a customer policy limit. */
const MAX_BROWSER_TIMEOUT_DELAY_MS = 2_147_483_647;

export type SupportProfileCommitGuard = () => boolean;

export function runGuardedSupportProfileCommit(
  commitGuard: SupportProfileCommitGuard,
  commit: () => void
): boolean {
  try {
    if (!commitGuard()) return false;
  } catch {
    return false;
  }

  commit();
  return true;
}

function decodeAndValidateUrlPart(value: string, rejectDotSegments: boolean): string | null {
  let current = value;

  for (let pass = 0; pass <= MAX_URL_DECODE_PASSES; pass += 1) {
    if (URL_WHITESPACE_RE.test(current) || UNSAFE_URL_TEXT_RE.test(current) || current.includes('\\')) {
      return null;
    }
    if (rejectDotSegments && current.split('/').some(segment => segment === '.' || segment === '..')) {
      return null;
    }

    let decoded: string;
    try {
      decoded = decodeURIComponent(current);
    } catch {
      return null;
    }

    if (decoded === current) return current;
    if (pass === MAX_URL_DECODE_PASSES) return null;
    current = decoded;
  }

  return null;
}

function hasExactDocsAuthority(input: string): boolean {
  if (!input.startsWith('https://')) return false;
  const authorityStart = 'https://'.length;
  const suffixStart = input.slice(authorityStart).search(/[/?#]/);
  const authorityEnd = suffixStart === -1 ? input.length : authorityStart + suffixStart;
  return input.slice(authorityStart, authorityEnd) === SUPPORT_AI_CUSTOMER_DOCS_AUTHORITY;
}

function rawPathFromUrl(input: string, absolute: boolean): string {
  const queryIndex = input.indexOf('?');
  const hashIndex = input.indexOf('#');
  const suffixStart = Math.min(
    queryIndex === -1 ? input.length : queryIndex,
    hashIndex === -1 ? input.length : hashIndex
  );

  if (!absolute) return input.slice(0, suffixStart);

  const authorityStart = input.indexOf('://') + 3;
  const pathStart = input.indexOf('/', authorityStart);
  return pathStart === -1 || pathStart >= suffixStart ? '/' : input.slice(pathStart, suffixStart);
}

function hasSafeQueryAndHash(input: string): boolean {
  const suffixStart = input.search(/[?#]/);
  if (suffixStart === -1) return true;
  return decodeAndValidateUrlPart(input.slice(suffixStart), false) !== null;
}

export function getSafeSupportAICustomerSourceHref(input: unknown): string | null {
  if (
    typeof input !== 'string' ||
    !input ||
    input.length > SUPPORT_AI_CUSTOMER_SOURCE_HREF_MAX_LENGTH ||
    input !== input.trim() ||
    URL_WHITESPACE_RE.test(input)
  ) {
    return null;
  }
  if (input.includes('\\') || !hasSafeQueryAndHash(input)) return null;

  const isAbsolute = hasExactDocsAuthority(input);
  if (!isAbsolute && (!input.startsWith('/') || input.startsWith('//'))) return null;

  if (isAbsolute) {
    let parsed: URL;
    try {
      parsed = new URL(input);
    } catch {
      return null;
    }

    if (
      parsed.origin !== SUPPORT_AI_CUSTOMER_DOCS_ORIGIN ||
      parsed.protocol !== 'https:' ||
      parsed.hostname !== 'docs.avalai.ir' ||
      parsed.username ||
      parsed.password ||
      parsed.port
    ) {
      return null;
    }
  }

  const decodedPath = decodeAndValidateUrlPart(rawPathFromUrl(input, isAbsolute), true);
  if (decodedPath === null || !decodedPath.startsWith('/')) return null;

  // An account-data page is only safe as the bare path: no query, no hash, no encoding tricks.
  if (!isAbsolute && SUPPORT_AI_CUSTOMER_ACCOUNT_PATHS.has(decodedPath)) {
    return input === decodedPath ? input : null;
  }

  if (
    !isAbsolute &&
    decodedPath !== SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH &&
    !decodedPath.startsWith(`${SUPPORT_AI_CUSTOMER_KNOWLEDGE_BASE_PATH}/`)
  ) {
    return null;
  }

  return input;
}

/** Keep only known account-tool names from an untrusted payload, in canonical order and without repeats. */
export function normalizeSupportAICustomerAccountTools(value: unknown): SupportAICustomerAccountTool[] {
  if (!Array.isArray(value)) return [];
  const present = new Set(value.filter((item): item is string => typeof item === 'string'));
  return SUPPORT_AI_CUSTOMER_ACCOUNT_TOOLS.filter(tool => present.has(tool));
}

interface SupportAICustomerReplyCandidate {
  author?: { author_type?: string | null } | null;
  customer_ai?: Api.Support.SupportAICustomerReplyMeta | null;
}

export function isVerifiedSupportAICustomerReply(
  reply: SupportAICustomerReplyCandidate
): reply is SupportAICustomerReplyCandidate & {
  customer_ai: Api.Support.SupportAICustomerReplyMeta;
} {
  return reply.author?.author_type === 'bot' && Boolean(reply.customer_ai);
}

export interface SupportAICustomerBootstrapInput {
  isVerifiedOwner: boolean;
  hasVerifiedAIContext: boolean;
  status: Api.Support.SupportAICustomerStatus | null;
  statusLoadFailed?: boolean;
}

export interface SupportAICustomerBootstrapDecision {
  shouldLoadStatus: boolean;
  shouldShowStatus: boolean;
  shouldPoll: boolean;
}

export function resolveSupportAICustomerBootstrap(
  input: SupportAICustomerBootstrapInput
): SupportAICustomerBootstrapDecision {
  const shouldLoadStatus = input.isVerifiedOwner;
  // Reserve one stable status region from the first lookup through an idle result or retry.
  const shouldShowStatus = shouldLoadStatus;
  const shouldPoll = shouldLoadStatus && input.status?.pending === true && (
    input.status.state === 'queued' ||
    input.status.state === 'processing' ||
    input.status.state === 'clarification_queued'
  );

  return { shouldLoadStatus, shouldShowStatus, shouldPoll };
}

export async function bootstrapSupportAICustomerStatus(
  input: Omit<SupportAICustomerBootstrapInput, 'status'>,
  loadAndSchedulePendingStatus: () => Promise<Api.Support.SupportAICustomerStatus | null>
): Promise<SupportAICustomerBootstrapDecision> {
  const initialDecision = resolveSupportAICustomerBootstrap({ ...input, status: null });
  if (!initialDecision.shouldLoadStatus) return initialDecision;

  const status = await loadAndSchedulePendingStatus();
  return resolveSupportAICustomerBootstrap({ ...input, status });
}

export function canShowSupportAICustomerHumanReviewAction(
  status: Api.Support.SupportAICustomerStatus | null,
  requestAllowed: boolean,
  ticketTerminal: boolean,
  ticketHumanRequestAvailable: boolean = false
): boolean {
  if (!requestAllowed || ticketTerminal) return false;
  if (status === null) return ticketHumanRequestAvailable === true;
  if (!status) return false;
  if (status.state === 'human_required') return false;
  return status.human_request_available === true &&
    status.human_intervention_pending !== true &&
    status.state !== 'human_requested';
}

const SUPPORT_AI_CUSTOMER_ENROLLMENT_DECLINE_REASONS = [
  'rollout_denied',
  'owner_mismatch',
  'category_mismatch',
  'category_denied',
  'attachment_present',
  'custom_fields_invalid',
  'sensitive_field_present',
  'network_digest_invalid',
  'budget_exhausted',
  'configuration_invalid',
  'enrollment_internal_error'
] as const satisfies readonly Api.Support.SupportAICustomerEnrollmentDeclineReason[];

export function getSupportAICustomerAgentEnrollmentDeclineReason(
  reason: unknown,
  hasAgentAccess: boolean
): Api.Support.SupportAICustomerEnrollmentDeclineReason | null {
  if (!hasAgentAccess || typeof reason !== 'string') return null;
  return (SUPPORT_AI_CUSTOMER_ENROLLMENT_DECLINE_REASONS as readonly string[]).includes(reason)
    ? reason as Api.Support.SupportAICustomerEnrollmentDeclineReason
    : null;
}

export function getSupportAICustomerAgentSessionHintKey(
  state: Api.Support.SupportAICustomerState,
  publishedCount: number
): 'handoffHint' | 'handoffAfterRepliesHint' | 'pendingHint' | 'answeredHint' {
  if (state === 'human_required' || state === 'human_requested' || state === 'stopped') {
    return publishedCount > 0 ? 'handoffAfterRepliesHint' : 'handoffHint';
  }
  if (isSupportAICustomerPendingState(state)) return 'pendingHint';
  return 'answeredHint';
}

function normalizeSupportPrincipalId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return normalized || null;
}

export function getAuthenticatedSupportPrincipalKey(
  isAuthenticated: unknown,
  uuid: unknown
): string | null {
  const principalId = normalizeSupportPrincipalId(uuid);
  if (isAuthenticated !== true || !principalId) return null;
  return JSON.stringify(['authenticated', principalId]);
}

export function isSupportPrincipalProfileCurrent(
  principalKey: string | null,
  profileId: unknown
): boolean {
  return principalKey !== null && principalKey === getAuthenticatedSupportPrincipalKey(true, profileId);
}

interface SupportPrincipalTransitionHooks {
  clearSensitiveState(): void;
  reloadForPrincipal(principalKey: string): Promise<void> | void;
}

export async function runSupportPrincipalTransition(
  previousPrincipalKey: string | null,
  nextPrincipalKey: string | null,
  hooks: SupportPrincipalTransitionHooks
): Promise<boolean> {
  if (previousPrincipalKey === nextPrincipalKey) return false;

  hooks.clearSensitiveState();
  if (nextPrincipalKey) await hooks.reloadForPrincipal(nextPrincipalKey);
  return true;
}

export function replaceWithAuthoritativeSupportTicket<T>(
  _previousTicket: unknown,
  serverTicket: T
): T {
  return serverTicket;
}

export interface SupportAIHumanReviewAttempt {
  logicalKey: string;
  idempotencyKey: string;
}

export function resolveSupportAIHumanReviewAttempt(
  currentAttempt: SupportAIHumanReviewAttempt | null,
  logicalKey: string,
  createIdempotencyKey: () => string
): SupportAIHumanReviewAttempt {
  if (currentAttempt?.logicalKey === logicalKey) return currentAttempt;
  return { logicalKey, idempotencyKey: createIdempotencyKey() };
}

export function settleSupportAIHumanReviewAttempt<T>(
  attempt: SupportAIHumanReviewAttempt,
  result: T | null
): SupportAIHumanReviewAttempt | null {
  return result === null ? attempt : null;
}

export function isSupportAICustomerPendingState(state: Api.Support.SupportAICustomerState): boolean {
  return state === 'queued' || state === 'processing' || state === 'clarification_queued';
}

export function isSupportAICustomerTerminalState(state: Api.Support.SupportAICustomerState): boolean {
  return (
    state === 'not_requested' ||
    state === 'answered' ||
    state === 'answered_after_clarification' ||
    state === 'human_requested' ||
    state === 'human_required' ||
    state === 'stopped'
  );
}

/** Public-only presentation decisions. Never inspect a server reason or draft body here. */
export function getSupportAICustomerStatusView(
  status: Api.Support.SupportAICustomerStatus | null,
  hasError: boolean
): {
  messageKey: string;
  showResult: boolean;
  resultReplyId: number | null;
  showRecheck: boolean;
} {
  if (!status) {
    return {
      messageKey: hasError ? 'statusUnavailable' : 'statusLoading',
      showResult: false,
      resultReplyId: null,
      showRecheck: hasError
    };
  }

  const messageKeyByOutcome: Record<Api.Support.SupportAICustomerOutcome, string> = {
    idle: 'statusNotRequested',
    generating: 'statusGenerating',
    clarification: 'statusClarification',
    answered: 'statusAnswered',
    agent_review: 'statusHumanRequired',
    failed: 'statusFailed',
    limit_reached: 'statusLimitReached',
    stopped: 'statusStopped'
  };
  const legacyMessageByState: Record<Api.Support.SupportAICustomerState, string> = {
    not_requested: 'statusNotRequested',
    queued: 'statusGenerating',
    processing: 'statusGenerating',
    clarification_queued: 'statusGenerating',
    clarification_requested: 'statusClarification',
    answered: 'statusAnswered',
    answered_after_clarification: 'statusAnswered',
    human_required: 'statusAutoRequested',
    human_requested: 'statusHumanRequested',
    stopped: 'statusStopped'
  };
  const outcome = status.outcome ?? 'idle';
  const resultReplyId =
    (outcome === 'answered' || outcome === 'clarification' || outcome === 'limit_reached') &&
    Number.isSafeInteger(status.latest_public_reply_id) &&
    (status.latest_public_reply_id ?? 0) > 0
      ? status.latest_public_reply_id ?? null
      : null;
  return {
    messageKey: status.state === 'human_required'
      ? 'statusAutoRequested'
      : status.state === 'human_requested'
      ? 'statusHumanRequested'
      : status.outcome
        ? messageKeyByOutcome[outcome] ?? 'statusUnavailable'
        : legacyMessageByState[status.state] ?? 'statusUnavailable',
    showResult: resultReplyId !== null,
    resultReplyId,
    showRecheck: true
  };
}

export function isSupportAICustomerPendingObservation(status: Api.Support.SupportAICustomerStatus): boolean {
  return status.pending === true && isSupportAICustomerPendingState(status.state) &&
    !isSupportAICustomerTerminalState(status.state);
}

export function isSupportAICustomerPollingPolicy(value: unknown): value is Api.Support.SupportAICustomerPollingPolicy {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const policy = value as Record<string, unknown>;
  if (!['minimum_interval_seconds', 'maximum_interval_seconds', 'max_duration_ms',
    'max_consecutive_failures', 'failure_backoff_ms'].every(key =>
      Object.hasOwn(policy, key) && Number.isSafeInteger(policy[key]) && (policy[key] as number) > 0)) return false;
  return (policy.minimum_interval_seconds as number) <= (policy.maximum_interval_seconds as number);
}

export function getSupportAICustomerPollDelay(
  status: Api.Support.SupportAICustomerStatus,
  elapsedMs: number,
  policy: Api.Support.SupportAICustomerPollingPolicy | null,
  failures = 0
): number | null {
  if (!isSupportAICustomerPendingObservation(status) || !isSupportAICustomerPollingPolicy(policy) ||
      !Number.isFinite(elapsedMs) || elapsedMs < 0 || elapsedMs >= policy.max_duration_ms ||
      !Number.isSafeInteger(failures) || failures < 0 ||
      !Number.isSafeInteger(status.poll_after_seconds) || status.poll_after_seconds <= 0) return null;
  const remainingMs = policy.max_duration_ms - elapsedMs;
  const ceiling = Math.min(remainingMs, MAX_BROWSER_TIMEOUT_DELAY_MS);
  const seconds = Math.min(policy.maximum_interval_seconds,
    Math.max(policy.minimum_interval_seconds, status.poll_after_seconds));
  // Saturate before multiplying: large valid wire integers must never overflow timers.
  const baseDelay = seconds >= ceiling / 1000 ? ceiling : seconds * 1000;
  const availableBackoff = ceiling - baseDelay;
  const backoff = failures >= availableBackoff / policy.failure_backoff_ms
    ? availableBackoff : failures * policy.failure_backoff_ms;
  return baseDelay + backoff;
}

export interface SupportAICustomerVoteTransition {
  previousVote: Api.Support.SupportAICustomerVote;
  optimisticVote: Api.Support.SupportAICustomerVote;
}

export function createSupportAICustomerVoteTransition(
  previousVote: Api.Support.SupportAICustomerVote,
  optimisticVote: Api.Support.SupportAICustomerVote
): SupportAICustomerVoteTransition {
  return { previousVote, optimisticVote };
}

export function rollbackSupportAICustomerVote(
  transition: SupportAICustomerVoteTransition
): Api.Support.SupportAICustomerVote {
  return transition.previousVote;
}
